import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { convertMinutesToHours, getLocalStorageData } from "@picovico/utils";
import { useGetNowShowingQuery } from "@store/services/movie";
import React, { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { IoSearch } from "react-icons/io5";
import { format } from "date-fns";
import { getPageRoutes } from "@/routes/pageRoutes";
import {
  AlertMessage,
  DynamicGrid,
  MovieCard,
  MovieCardListSkeleton,
} from "@picovico/ui";
import { NO_MOVIE } from "@picovico/ui/constant/alertMessage";

export function SearchMovies({ ...props }) {
  const [search, setSearch] = useState<string>();
  const [debouncedSearch, setDebouncedSearch] = useState<string>();
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const cinema_hall = getLocalStorageData("cinema_hall");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(handler);
  }, [search]);

  const { isLoading, data, isFetching } = useGetNowShowingQuery(
    {
      search: debouncedSearch,
      advance_days: true,
    },
    {
      skip: !debouncedSearch,
    },
  );

  const genreList = Array.from(
    new Set(
      data?.data
        .flatMap((movie) =>
          Array.isArray(movie.genre)
            ? movie.genre
            : typeof movie.genre === "string"
              ? movie.genre.split(",").map((g) => g.trim())
              : [],
        )
        .filter(Boolean),
    ),
  );

  const movies =
    data?.data
      ?.filter((movie) => {
        if (!selectedGenre) return true;
        if (Array.isArray(movie.genre)) {
          return movie.genre.includes(selectedGenre);
        }
        if (typeof movie.genre === "string") {
          return movie.genre
            .split(",")
            .map((g) => g.trim())
            .includes(selectedGenre);
        }
        return false;
      })
      .map(
        ({
          idx,
          grade,
          duration,
          poster,
          release_date,
          genre,
          ...item
        }) => ({
          title: item.name,
          poster,
          grade,
          genre,
          duration: convertMinutesToHours(duration),
          idx,
          releaseDate: format(new Date(release_date), "MMMM d yyyy"),
          videoLink: item.trailer,
          detailLink: getPageRoutes("movie_detail", {
            movieIdx: idx,
            cinemaHallIdx: cinema_hall as string,
          }),
          isNowShowing: true,
          showTimeList: false,
        }),
      ) || [];

  return (
    <>
      <Flex justifyContent={"end"} w="full" mr={"5px"}>
        <InputGroup
          width={search ? "25%" : "40px"}
          size="sm"
          _hover={{
            width: "25%",
            border: "1px solid #007AC0",
            rounded: "full",
          }}
          transition="width 0.3s ease"
        >
          <Input
            placeholder={"Search"}
            sx={{
              rounded: "full",
              border: "1px solid #007AC0",
              backgroundColor: "#E2F4FF",
            }}
            _placeholder={{
              color: "#007AC0",
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <InputRightElement pointerEvents="none" m={0}>
            <IoSearch color="#007AC0" size={"20px"} />
          </InputRightElement>
        </InputGroup>
      </Flex>
      {search && (
        <Box
          position="fixed"
          top="124px"
          left={0}
          right={0}
          bottom={0}
          zIndex={1400}
          bg="rgba(8, 8, 8, 0.8)"
          display="flex"
          onClick={(e) => e.stopPropagation()}
          p={6}
        >
          <Box
            opacity={1}
            top={0}
            w="full"
            mx={14}
            overflow={"auto"}
            sx={{
              "::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <Button
              onClick={() => setSearch("")}
              gap={4}
              variant="outline"
              backgroundColor={"white"}
              size={"sm"}
            >
              <FaArrowLeft /> <Text>Go Back</Text>
            </Button>
            <HStack mt="4">
              <Text color="white" fontWeight={"bold"}>
                Search Titles Related To :
              </Text>
              <Flex gap={3}>
                <Button
                  key="all"
                  variant={selectedGenre === null ? "solid" : "outline"}
                  backgroundColor={
                    selectedGenre === null ? "#007AC0" : "#abcdecff"
                  }
                  color={selectedGenre === null ? "white" : "black"}
                  size="sm"
                  onClick={() => setSelectedGenre(null)}
                >
                  All
                </Button>
                {genreList.map((genre) => (
                  <Button
                    key={genre}
                    variant={selectedGenre === genre ? "solid" : "outline"}
                    backgroundColor={
                      selectedGenre === genre ? "#007AC0" : "#abcdecff"
                    }
                    color={selectedGenre === genre ? "white" : "black"}
                    size="sm"
                    onClick={() =>
                      setSelectedGenre(selectedGenre === genre ? null : genre)
                    }
                  >
                    {genre}
                  </Button>
                ))}
              </Flex>
            </HStack>
            <Divider my="4" />
            <Box mt={6}>
              <Flex gap={5}>
                <Text
                  color="white"
                  fontSize={"18px"}
                  display="flex"
                  textAlign={"center"}
                >
                  Search Results:{" "}
                </Text>
                <Text
                  fontSize={"12px"}
                  display="flex"
                  textAlign={"center"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  color="white"
                >
                  {movies.length} results
                </Text>
              </Flex>

              <Box mt={4} overflow={"auto"} height="fit-content">
                {search !== debouncedSearch || isLoading || isFetching ? (
                  <MovieCardListSkeleton
                    grid={{
                      base: 300,
                      md: 300,
                      lg: 350,
                    }}
                  />
                ) : movies.length < 1 ? (
                  <AlertMessage
                    message={NO_MOVIE}
                    sx={{
                      mt: "1rem",
                    }}
                  />
                ) : (
                  <DynamicGrid
                    key={`movie__tab__gird__panes+`}
                    baseChildKey={`movie_grid`}
                    grid={{
                      base: 300,
                      md: 300,
                      lg: 350,
                    }}
                  >
                    {movies?.map((movie, movieIndex: number) => (
                      <MovieCard
                        key={`movie__card__panes__${movieIndex}`}
                        {...props}
                        {...movie}
                      />
                    ))}
                  </DynamicGrid>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
